import React, { useState, useRef, useEffect } from 'react';
import { createClaudeResponse } from '../chat/openAIUtils';
import AmfiSlideContent from './AmfiSlideContent';
import { RenderMarkdown } from '../chat/RenderMarkdown';
import { FaSpinner, FaFileWord, FaFilePowerpoint } from 'react-icons/fa';
import { exportAmfiAsWord } from './AmfiExportAsWord';
import { exportAmfiAsPptx } from './AmfiExportAsPptx';
import './AmfiEdu.css';

const AMFI_MODEL = 'claude-sonnet-4-20250514';

// ─────────────────────────────────────────────────────────────────────────────
// SHARED CHAT PROMPT  (conversational, no JSON)
// ─────────────────────────────────────────────────────────────────────────────
const AMFI_CHAT_PROMPT = `You are the AMFI Learning Companion — an AI tutor built on the Islamic Integrated Learning Framework.
You support two models that the user may be using:

MODEL A — AMFI × 5DT (Integrated):
Tafakkur (Think) × Analytical → Tadabbur (Reflect) × Analogical → Iʿtibār (Learn) × Critical → Tadhakkur (Remember) × Meditative → Khilāfah (Act) × Moral

MODEL B — Pure AMFI (Qur'anic Learning Chain):
Tafakkur (Think) → Tadabbur (Reflect) → Tadhakkur (Remember) → Iʿtibār (Learn) → Khilāfah (Act)
Supporting lenses (woven throughout): Muslim Scholar Spotlight · Creation vs Human Design · Revelation Anchors · Names of Allah

In conversation mode, be curious and Socratic. Never generate structured data — just have a thoughtful educational conversation.

CORE RULES:
- Never attribute anything to "nature," "natural processes," "chance," or "random evolution" — use "designed," "arranged," "purposefully structured"
- Never introduce Allah or divine attribution before the Connect/Tadhakkur step in a lesson sequence
- Science and revelation are not in conflict — science deepens faith through reasoning`;

// ─────────────────────────────────────────────────────────────────────────────
// SYSTEM PROMPT A — AMFI × 5DT (existing model, unchanged)
// ─────────────────────────────────────────────────────────────────────────────
const AMFI_5DT_GEN_PROMPT = `You are an AI implementing the AMFI × 5DT Learning Framework. You generate structured lesson content per dimension. Follow all output formats exactly.

CORE PHILOSOPHY:
The AMFI × 5DT approach integrates scientific reasoning with Islamic understanding. Each step builds on the previous. The conclusion must emerge from reasoning, not assumption.
Steps: Objectives → Explore → Compare → Question → Connect → Appreciate → Activities

TERMINOLOGY RULES (APPLY TO ALL DIMENSIONS EXCEPT QUESTION):
FORBIDDEN: "nature," "natural," "mother nature," "naturally," "luck," "by chance," "randomly," "random selection," "evolved," "adapted," "natural processes," "natural laws"
REPLACEMENTS: "nature" → "environment/creation" | "natural" → "designed/arranged" | "evolved" → "designed/made to function" | "by chance" → avoid; describe intentional design
IN QUESTION ONLY: use forbidden terms deliberately to expose their inadequacy.

GRADE LEVEL: All grades: core content, facts, basic analogy, foundational questions. Grade 3+: developed analogies, names of Allah, Qur'anic connections. Grade 7+: emergent properties, deeper questioning, full Qur'an/Hadith, continuous creation, nuanced character lessons.

OBJECTIVES — Return ONLY this JSON:
{"dimension":"Objectives","learningObjectives":{"recall":["Recall [specific facts about topic]..."],"negation_of_chance_material_causes_and_nature":["Justify why chance, material causes, and autonomous processes cannot explain [topic]..."],"interconnectivity":["Identify the interconnectivity between [topic] and the wider universe..."],"names_of_Allah":["Identify at least 5 Divine Names of Allah reflected in [topic]..."],"blessings":["Discuss the purposeful, customized blessings Allah has provided through [topic]..."],"character_lessons":["Derive at least 3 character lessons from [topic] covering professional, moral, and spiritual growth..."]}}

EXPLORE (Tafakkur × Analytical) — Return ONLY this JSON:
{"dimension":"Explore","content":"Engaging, ideology-free description of [topic] as purposefully designed. Bold key terms with **term**. Scientifically accurate.","observations":"Key observable features inspiring analytical thinking — frame as evidence of intentional design.","fascinatingFacts":["Did you know... [awe-inspiring fact 1]","Did you know... [awe-inspiring fact 2]","Did you know... [awe-inspiring fact 3]","Did you know... [awe-inspiring fact 4]","Did you know... [awe-inspiring fact 5]"]}

COMPARE (Tadabbur × Analogical) — Return ONLY this JSON:
{"dimension":"Compare","analogy":"The chosen human-made analogy and why it is the most fitting comparison for [topic]...","content":"How this analogy maps onto [topic] — specific parallel components...","explanation":"Detailed comparison of similarities and differences — where the human-made system falls short while [topic] operates seamlessly...","comparison":"Component-by-component comparison with at least 4-5 pairs. Conclude with: 'Just as [analogy] requires knowledge, will, and power from its maker — so too does [topic] demand these same qualities, yet at a scale and precision that no human engineer could replicate.'"}

QUESTION (Iʿtibār × Critical) — Return ONLY this JSON:
{"dimension":"Question","questions":{"negation_of_chance":["Question targeting a specific, precisely calibrated property — asking WHY that exact specification and not another...","Question using the Compare analogy — if we found [analogy] with these exact specifications would we attribute it to chance? Then why [topic]?","Question targeting a second specific property with a numerical/observable specification..."],"negation_of_material_causes":["Question asking whether the raw materials of [topic] possess the knowledge or will to arrange themselves...","Question using the Compare analogy — do the materials of [analogy] decide how to assemble themselves?...","Question targeting an emergent property that does not exist in its components — where did that property come from?..."],"negation_of_nature":["What is 'nature' actually — can we point to it, weigh it, observe its knowledge or will?...","If laws govern [topic], who established those laws and why do they operate with such precision?...","Is 'nature' as an explanation more or less satisfying than 'an unknown engineer built this'?..."]},"conclusion":"Having examined [topic] through chance, material causation, and 'nature,' none of these explanations can account for the knowledge, will, and power required. Just as [analogy] demands a knowing maker — so too must [topic]. The secular trio is insufficient."}

CONNECT (Tadhakkur × Meditative) — Return ONLY this JSON:
{"dimension":"Connect","connections":{"interconnectedness":"How [topic] is connected to the wider universe at macro and micro levels. Nothing exists in isolation.","completeDependency":"How every component of [topic] is completely dependent on other systems. Include 2-3 emergent properties that don't exist in individual components. Include continuous creation: [topic] exists only through Allah's continuous active maintenance, like a live broadcast that would cease the moment the broadcaster stops."},"allahNames":{"namesInEnglish":["Name1","Name2","Name3","Name4","Name5"],"namesInArabic":["اسم١","اسم٢","اسم٣","اسم٤","اسم٥"],"whatItTells":["[Name1] — [Arabic] — meaning [meaning] — is manifest here because Allah [active verb]... through [topic]. [Topic] is not the source; it is the medium. We see this specifically in [observable feature]...","[Name2] entry...","[Name3] entry...","[Name4] entry...","[Name5] entry..."]}}
CRITICAL: Allah is ALWAYS the active agent. [Topic] is ALWAYS the instrument, never the source. Every whatItTells entry must reflect this.

APPRECIATE (Khilāfah × Moral) — Return ONLY this JSON:
{"dimension":"Appreciate","whatIfs":"2-3 what-if scenarios that make the student feel the immense value of [topic] and provoke genuine gratitude.","zikrFikrShukr":{"zikr":["Remembrance 1: Allah alone possesses the knowledge and power that [topic] requires...","Remembrance 2: Every moment [topic] functions is a moment of Allah's active continuous creation..."],"fikr":["Reflection 1: Ponder the wisdom behind [specific design feature]...","Reflection 2: Contemplate how [topic] serves as a bridge between micro and macro..."],"shukr":["Gratitude 1: Express gratitude for [specific customized benefit]...","Gratitude 2: Thank Allah for granting us the intellect to study [topic]..."]},"characterLessons":"3 character lessons: (1) Professional — precision and specialization as seen in [topic]; (2) Moral — interdependency and giving without return; (3) Spiritual — continuous gratitude, recognizing our existence depends on Allah as [topic] depends on its Maker.","connectWithQuran":[{"verse":"Complete accurate Arabic Qur'anic verse","translation":"Accurate English translation","explanation":"How this verse illuminates [topic]"}],"connectWithHadith":[{"hadith":"Authentic hadith with source","explanation":"How this hadith applies to [topic]"}],"dua":"A meaningful dua drawing together all five steps — acknowledge Allah as Creator, express gratitude, ask for wisdom."}

ACTIVITIES — Return ONLY this JSON:
{"dimension":"Activities","activities":[{"dimension":"Explore","title":"Activity title","objective":"To analytically observe [topic]...","materials":["material1","material2"],"instructions":["Step 1...","Step 2...","Step 3...","Step 4..."]},{"dimension":"Compare","title":"Build the Analogy","objective":"...","materials":["..."],"instructions":["...","...","...","..."]},{"dimension":"Question","title":"The Detective Challenge","objective":"...","materials":["..."],"instructions":["...","...","...","..."]},{"dimension":"Connect","title":"The Web of Creation","objective":"...","materials":["..."],"instructions":["...","...","...","..."]},{"dimension":"Appreciate","title":"Gratitude Map","objective":"...","materials":["..."],"instructions":["...","...","...","..."]}]}

RULES:
🔴 NEVER introduce Allah before the Connect step
🔴 NEVER use forbidden secular terms outside Question
🔴 NEVER produce vague fascinating facts
🔴 NEVER produce Compare without the mandatory concluding statement
🔴 NEVER write whatItTells where topic is the active agent
🔴 NEVER fabricate Qur'anic text or Hadith
🔴 NEVER leave placeholder text in output
🔴 Output must be valid JSON ONLY — no text outside JSON`;

// ─────────────────────────────────────────────────────────────────────────────
// SYSTEM PROMPT B — Pure AMFI (Qur'anic Learning Chain, no 5DT labels)
// ─────────────────────────────────────────────────────────────────────────────
const AMFI_PURE_GEN_PROMPT = `You are an AI implementing the Pure AMFI Educational Framework — a Qur'anic learning chain rooted in Ibn al-Qayyim's philosophy of thought-to-action. You generate structured lesson content per stage. Follow all output formats exactly.

CORE PHILOSOPHY:
Vision: To nurture children who see no divide between revelation and reason, approaching learning as an act of worship (ibadah).
Philosophical root: Ibn al-Qayyim's chain — Fikr (thought) → Ilm (knowledge) → Hāl (inner state) → Irādah (will) → Amal (action). Behavior is shaped upstream by how thoughts are formed, organized, and internalized.
Knowledge is never neutral. All learning carries moral weight and accountability.

THE FIVE STAGES (in order):
Objectives → Tafakkur → Tadabbur → Tadhakkur → Iʿtibār → Khilāfah → Activities

FOUR RECURRING LENSES (woven throughout all stages, not separate steps):
1. Muslim Scholar/Scientist Spotlight — introduce a Muslim scholar who engaged with this subject
2. Creation vs Human Design — compare what Allah created to the best human-made equivalent; highlight the gap
3. Revelation Anchors — Qur'anic ayat and authentic hadith as the interpretive framework
4. Names of Allah (Asma ul-Husna) — connect the subject to Divine Names; Allah is ALWAYS the active agent

TERMINOLOGY RULES (ALL STAGES EXCEPT Iʿtibār):
FORBIDDEN: "nature," "natural," "mother nature," "naturally," "luck," "by chance," "randomly," "evolved," "adapted," "natural processes," "natural laws"
REPLACEMENTS: "nature" → "creation/environment" | "natural" → "designed/arranged/purposefully structured" | "evolved" → "designed/made to function" | "by chance" → avoid entirely
IN Iʿtibār ONLY: use forbidden terms deliberately to expose their inadequacy as explanations.

GRADE LEVEL: All grades: core content, facts, foundational inquiry. Grade 3+: analogies, Names of Allah, Qur'anic connections. Grade 7+: deeper critical questioning, full Qur'an/Hadith references, emergent properties, continuous creation concept.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
OBJECTIVES — Return ONLY this JSON:
{"dimension":"Objectives","learningObjectives":{"recall":["Recall [specific key facts about topic]..."],"negation_of_chance_material_causes":["Articulate why chance and material causes alone cannot explain [topic]..."],"interconnectivity":["Identify how [topic] connects to the wider creation and other systems..."],"names_of_Allah":["Identify at least 5 Divine Names of Allah reflected in [topic], with Allah named as the active agent..."],"blessings":["Describe the purposeful, customized blessings Allah has provided through [topic]..."],"character_lessons":["Derive at least 3 character lessons from [topic] covering professional, moral, and spiritual growth..."]}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TAFAKKUR (Think) — Deliberate intellectual observation and contemplation
Core inquiry: "What is really going on here?"
Qur'anic anchor: 3:191 — give thought to the creation of the heavens and the earth

Include:
- Scientific/factual content about [topic] framed as a purposefully designed system (no forbidden terms)
- Key observable features that inspire wonder and analytical thinking
- Exactly 5 "Did you know..." fascinating facts (awe-inspiring, scientifically accurate)
- Muslim Scholar/Scientist Spotlight: a Muslim scholar who engaged with this subject — name, era, contribution, Islamic connection
- The "Filter": guide students to look past "chance" or autonomous processes as explanations

Return ONLY this JSON:
{"dimension":"Tafakkur","coreInquiry":"What is really going on here?","content":"Engaging, ideology-free description of [topic] framed as a deliberately arranged system. Bold key terms with **term**. Scientifically accurate.","observations":"Key observable features of [topic] that inspire wonder — frame as evidence of intentional, purposeful design.","fascinatingFacts":["Did you know... [awe-inspiring fact 1]","Did you know... [awe-inspiring fact 2]","Did you know... [awe-inspiring fact 3]","Did you know... [awe-inspiring fact 4]","Did you know... [awe-inspiring fact 5]"],"muslimScholar":{"name":"Scholar name and era","contribution":"What this scholar discovered or wrote about this subject","islamicConnection":"How their study of this subject was an act of Tafakkur and worship"},"creationVsDesign":{"humanDesign":"The best human-made equivalent of [topic]","divineCreation":"[Topic] as Allah arranged it","comparisonPairs":[{"human":"[Human design component]","divine":"[Corresponding feature in topic]"},{"human":"...","divine":"..."},{"human":"...","divine":"..."},{"human":"...","divine":"..."},{"human":"...","divine":"..."}],"gapStatement":"Where the human design falls short and what this reveals about the scale of knowledge required to arrange [topic]"}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TADABBUR (Reflect) — Following thoughts to their consequences and implications
Core inquiry: "What if this didn't exist — what would be lost?"
Qur'anic anchor: 47:24 — do they not reflect upon the Qur'an?

Include:
- Reflection on the consequences of [topic]'s existence — what depends on it, what would fail without it
- Reflection questions that trace consequences rather than surface observations
- Connection to the Creation vs Human Design comparison from Tafakkur

Return ONLY this JSON:
{"dimension":"Tadabbur","coreInquiry":"What if this didn't exist — what would be lost?","content":"Deep reflection on [topic] — what it enables, what it connects to, and what the consequences of its absence would be. Trace the implications outward from micro to macro level.","reflectionQuestions":["Reflection question 1 — tracing a specific consequence of [topic]'s design...","Reflection question 2 — following the loss of one component through the whole system...","Reflection question 3 — connecting the design of [topic] to purpose beyond itself..."],"designWisdom":"What the specific design choices in [topic] reveal about the wisdom required to arrange them — building on the Tafakkur comparison."}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TADHAKKUR (Remember) — Conscious remembrance that connects knowledge back to Allah
Core inquiry: "What does this teach me about Allah — and am I living in awareness of this?"
Qur'anic anchor: 13:19 — they will remember, those of understanding

Include:
- Connection of [topic] to Divine Names (exactly 5) with Allah as active agent and [topic] as instrument/medium
- A Qur'anic verse that speaks to this phenomenon
- The concept of continuous creation — [topic] is not self-sustaining; it exists through Allah's continuous active sustaining
- Interconnectivity at macro and micro levels

DIVINE NAMES RULE — CRITICAL:
Allah is ALWAYS the active agent. [Topic] is ALWAYS the instrument or medium — never the source.
❌ WRONG: "Al-Razzaq is seen in how soil provides nutrients"
✅ CORRECT: "Ar-Razzāq is manifest here because Allah provides sustenance through the medium of soil. Soil has no power to provide on its own — it is the arrangement through which Allah's act of provision becomes visible."

Return ONLY this JSON:
{"dimension":"Tadhakkur","coreInquiry":"What does this teach me about Allah — and am I living in awareness of this?","interconnectedness":"How [topic] is connected to the wider creation at macro level (cosmic, ecosystem) and micro level (cellular, atomic). Nothing exists in isolation.","continuousCreation":"Explanation of how [topic] is not self-sustaining — it exists only through Allah's continuous active maintaining, like a live broadcast that would cease the moment the broadcaster stops.","allahNames":{"namesInEnglish":["Name1","Name2","Name3","Name4","Name5"],"namesInArabic":["اسم١","اسم٢","اسم٣","اسم٤","اسم٥"],"whatItTells":["[Name1] — [Arabic] — meaning [meaning] — is manifest here because Allah [active verb]... through [topic]. [Topic] is not the source; it is the medium. We see this specifically in [observable feature of topic]...","[Name2] entry...","[Name3] entry...","[Name4] entry...","[Name5] entry..."]},"revelationAnchor":{"verse":"Complete accurate Arabic Qur'anic verse (verify at quran.com — if uncertain of exact Arabic wording, provide the reference and faithful translation only)","reference":"Surah name and ayah number","translation":"Accurate English translation","explanation":"How this verse specifically illuminates [topic] and deepens the lesson"}}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Iʿtibār (Learn) — Crossing from observation to moral wisdom; extracting the lesson
Core inquiry: "What lesson does this teach me — about life, consequences, and who I want to be?"
Qur'anic anchor: 59:2 — take warning, O you who have vision

IN THIS STAGE ONLY: deliberately use terms like "nature," "chance," "material causes," "natural laws" in order to expose their inadequacy as explanations. This is the only stage where these terms appear.

Include:
- Three categories of critical questioning (chance, material causes, autonomous processes)
- Questions must target SPECIFIC, PRECISELY CALIBRATED properties of [topic] — not vague binaries
- Each question identifies a precise specification of [topic] and asks WHY that exact value and not another
- Moral/civilizational lesson extracted from [topic] (not just scientific)
- Historical lesson: what happened to peoples or civilizations that misused or ignored this knowledge

QUESTION FORMULA: (1) Identify a specific property of [topic] (2) State its precise specification or numerical value (3) Ask WHY that exact value — not more, not less (4) Ask what would happen if it were slightly different (5) Make the student feel the insufficiency of the secular explanation

Return ONLY this JSON:
{"dimension":"Iʿtibār","coreInquiry":"What lesson does this teach me — about life, consequences, and who I want to be?","questions":{"negation_of_chance":["Specific question targeting a precisely calibrated property of [topic] — WHY that exact specification and not another? Make chance feel genuinely insufficient...","Question using the Creation vs Human Design comparison from Tafakkur — if we found the human-made version built to these exact specifications, would we attribute it to chance?...","Question targeting a second specific property with a numerical or observable specification..."],"negation_of_material_causes":["Question asking whether the specific raw materials of [topic] — name them — possess the knowledge or will to arrange themselves into this functioning system...","Question targeting an emergent property of [topic] that does not exist in its individual components — where did that property come from if not from the materials?...","Question connecting back to the human design comparison — do the raw materials of the human-made version assemble themselves?..."],"negation_of_autonomous_processes":["What is 'nature' or 'natural process' actually — can we point to it, observe its knowledge or will, or measure its intention?...","If laws govern [topic] — who established those laws, and why do they operate with such unwavering consistency and precision?...","What is the meaningful difference between saying 'autonomous processes did this' and 'an unknown designer of unlimited knowledge arranged this'?..."]},"conclusion":"Having examined [topic] through chance, material causes, and autonomous processes, none of these explanations accounts for the knowledge, will, and precision required. Just as our comparison in Tafakkur showed that the human-made version requires a knowing maker — so too does [topic] require One of unlimited knowledge and power. The raw materials have no knowledge. Chance cannot produce consistent precision. 'Natural processes' is a label, not an explanation.","historicalLesson":"What happened to peoples, civilizations, or ecosystems that misused or ignored this aspect of creation — and what does that teach us?","moralLesson":"The deeper moral or civilizational truth that [topic] teaches — what it reveals about human responsibility, consequences, and accountability."}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
KHILĀFAH (Act) — Responsible trusteeship; translating knowledge into ethical action
Core inquiry: "What am I now responsible to do or change — as a trustee of this knowledge?"
Qur'anic anchor: 6:165 — He has made you successors upon the earth

Include:
- What-if scenarios that make the value of [topic] viscerally felt (provoking genuine gratitude/shukr)
- Dhikr (remembrance), Fikr (pondering), Shukr (gratitude)
- Character lessons: professional, moral, and spiritual
- Concrete stewardship actions students can take
- Qur'anic verse + authentic hadith
- Closing dua

Return ONLY this JSON:
{"dimension":"Khilāfah","coreInquiry":"What am I now responsible to do or change — as a trustee of this knowledge?","whatIfs":"2-3 what-if scenarios or brief real stories that make the student feel the immense, customized value of [topic] and provoke genuine gratitude.","zikrFikrShukr":{"zikr":["Remembrance 1: Allah alone possesses the infinite knowledge and power that [topic] requires — it did not arrange itself...","Remembrance 2: Every moment [topic] functions is a moment of Allah's active, continuous sustaining — not a self-running clock..."],"fikr":["Reflection 1: Ponder the wisdom behind [specific design feature of topic] — why was it made this way and not another?...","Reflection 2: Contemplate how [topic] connects the micro and macro levels of creation in perfect harmony..."],"shukr":["Gratitude 1: Express gratitude for [specific customized benefit of topic] that sustains life uniquely...","Gratitude 2: Thank Allah for granting us the intellect to study, understand, and recognize the signs in [topic]..."]},"characterLessons":{"professional":"Character lesson derived from [topic] for professional life — precision, patience, specialization...","moral":"Character lesson derived from [topic] for moral/interpersonal life — giving without recognition, interdependency, service...","spiritual":"Character lesson derived from [topic] for spiritual life — humility, continuous gratitude, recognizing our dependence on Allah..."},"stewardshipActions":["Concrete action 1 students can take as trustees of this knowledge...","Concrete action 2 — practical stewardship or behavioral change...","Concrete action 3 — sharing or teaching what was learned..."],"connectWithQuran":[{"verse":"Complete accurate Arabic Qur'anic verse (verify at quran.com — if uncertain of exact Arabic wording, provide reference and faithful translation only)","reference":"Surah name and ayah number","translation":"Accurate English translation","explanation":"How this verse specifically illuminates [topic] and its khilāfah dimension"}],"connectWithHadith":[{"hadith":"Authentic hadith text with source reference — verify at sunnah.com. If uncertain of exact wording, state the collection and note the wording should be verified.","explanation":"How this hadith applies to [topic] and what practical guidance it offers"}],"dua":"A meaningful, contextually relevant dua drawing together all five stages — acknowledge Allah as Creator, express gratitude for the gift, ask for wisdom to act as faithful trustees."}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ACTIVITIES — One activity per stage
Return ONLY this JSON:
{"dimension":"Activities","activities":[{"stage":"Tafakkur","title":"Activity title — Observe and Think","objective":"To carefully observe [topic] and begin asking WHY and HOW, not accepting surface appearances.","materials":["material1","material2","material3"],"instructions":["Step 1: ...","Step 2: Experiment — [describe a hands-on experiment relevant to topic]","Step 3: ...","Step 4: Discussion — what did we observe that cannot be explained by chance or material causes alone?"]},{"stage":"Tadabbur","title":"The Consequence Map — What If This Didn't Exist?","objective":"To trace the consequences of [topic]'s design choices and feel the weight of what depends on it.","materials":["..."],"instructions":["Step 1: Present the Creation vs Human Design comparison physically.","Step 2: Students map what fails if one component of [topic] is removed.","Step 3: ...","Step 4: Students derive: both systems require knowledge, will, and power from a maker."]},{"stage":"Iʿtibār","title":"The Detective Challenge — Could This Have Happened Alone?","objective":"To critically examine chance, material causes, and autonomous processes as explanations for [topic].","materials":["Question prompt cards","Scenario worksheets"],"instructions":["Step 1: ...","Step 2: Students work in pairs on question prompt cards targeting specific, calibrated properties.","Step 3: Class debate.","Step 4: Class reaches the shared conclusion that none of the three explanations suffice."]},{"stage":"Tadhakkur","title":"The Names of Allah in [Topic] — Mapping the Signs","objective":"To identify Divine Names reflected in [topic] with Allah as the active agent, and connect [topic] to its Source.","materials":["Large poster paper","Divine Names reference cards","Colored markers"],"instructions":["Step 1: Students create a mind-map of [topic]'s interconnections.","Step 2: Students match Divine Names to aspects of [topic] — writing entries with Allah as the active agent.","Step 3: Discuss continuous creation — add sustaining arrows to the map.","Step 4: Read the Qur'anic verse together. Moment of Tadhakkur — silence and presence."]},{"stage":"Khilāfah","title":"Gratitude Map — The Trust We Received","objective":"To reflect on [topic] as a customized gift and commit to faithful stewardship.","materials":["Gratitude map template","What-if scenario cards","Character lesson reflection sheet"],"instructions":["Step 1: Present what-if scenario — 'What if [key property of topic] did not exist?' Students write their response.","Step 2: Students complete Gratitude Map — zikr, fikr, and shukr responses.","Step 3: Students identify one professional, one moral, one spiritual character lesson.","Step 4: Close with the dua, recited together as a class."]}]}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
ABSOLUTE RULES:
🔴 NEVER introduce Allah or divine attribution before the Tadhakkur stage
🔴 NEVER use forbidden secular terms outside the Iʿtibār stage
🔴 NEVER write allahNames entries where [topic] is the active agent — Allah is ALWAYS the active agent
🔴 NEVER produce vague or binary questions in Iʿtibār — every question must target a specific, precisely calibrated property
🔴 NEVER fabricate Qur'anic Arabic text or Hadith wording — cite the reference and provide a faithful translation if uncertain
🔴 NEVER leave placeholder text like "[topic]" in the final output — every field must be fully realized
🔴 Output must be valid JSON ONLY — no text outside JSON structure`;

// ─────────────────────────────────────────────────────────────────────────────
// DIMENSION CONFIGS
// ─────────────────────────────────────────────────────────────────────────────
const AMFI_5DT_DIMENSIONS = [
  { name: 'Objectives', title: 'Learning Objectives' },
  { name: 'Explore',    title: 'Tafakkur × Analytical — Explore' },
  { name: 'Compare',    title: 'Tadabbur × Analogical — Compare' },
  { name: 'Question',   title: "Iʿtibār × Critical — Question" },
  { name: 'Connect',    title: 'Tadhakkur × Meditative — Connect' },
  { name: 'Appreciate', title: 'Khilāfah × Moral — Appreciate' },
  { name: 'Activities', title: 'Activities' },
];

const AMFI_PURE_DIMENSIONS = [
  { name: 'Objectives', title: 'Learning Objectives' },
  { name: 'Tafakkur',   title: 'Tafakkur — Think' },
  { name: 'Tadabbur',   title: 'Tadabbur — Reflect' },
  { name: 'Tadhakkur',  title: 'Tadhakkur — Remember' },
  { name: 'Iʿtibār',   title: "Iʿtibār — Learn" },
  { name: 'Khilāfah',  title: 'Khilāfah — Act' },
  { name: 'Activities', title: 'Activities' },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS (shared)
// ─────────────────────────────────────────────────────────────────────────────
const extractClaudeResponseText = (response) => {
  if (!response) return '';

  if (typeof response.completion === 'string' && response.completion.trim()) {
    return response.completion;
  }

  if (typeof response.output_text === 'string' && response.output_text.trim()) {
    return response.output_text;
  }

  if (Array.isArray(response.output_text)) {
    return response.output_text.filter(Boolean).join('');
  }

  if (!Array.isArray(response.content)) {
    return '';
  }

  return response.content
    .map((part) => {
      if (typeof part?.text === 'string') return part.text;
      if (typeof part?.text?.value === 'string') return part.text.value;
      return '';
    })
    .join('');
};

const normalizePrefilledJsonText = (rawText) => {
  const text = (rawText || '').trim();
  if (!text) return '';
  if (text.startsWith('{') || text.startsWith('[') || text.startsWith('```')) {
    return text;
  }
  return `{${text}`;
};

const extractBalancedJsonSlice = (source) => {
  const text = (source || '').trim();
  if (!text) return '';

  let start = -1;
  const stack = [];
  let inString = false;
  let escapeNext = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (start === -1) {
      if (char === '{' || char === '[') {
        start = i;
        stack.push(char);
      }
      continue;
    }

    if (inString) {
      if (escapeNext) {
        escapeNext = false;
      } else if (char === '\\') {
        escapeNext = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === '{' || char === '[') {
      stack.push(char);
      continue;
    }

    if (char === '}' || char === ']') {
      const expected = char === '}' ? '{' : '[';
      if (stack[stack.length - 1] !== expected) {
        continue;
      }
      stack.pop();
      if (stack.length === 0) {
        return text.slice(start, i + 1);
      }
    }
  }

  return '';
};

const parseJsonPayload = (rawText) => {
  const text = (rawText || '').trim();
  if (!text) throw new Error('Empty response from model');

  const candidates = new Set();
  const addCandidate = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    candidates.add(trimmed);

    let nested = trimmed;
    while (
      (nested.startsWith('{') && nested.endsWith('}')) ||
      (nested.startsWith('[') && nested.endsWith(']'))
    ) {
      nested = nested.slice(1, -1).trim();
      if (!nested) break;
      candidates.add(nested);
    }
  };

  addCandidate(text);

  // Strip all code fences (not just outer ones)
  const noFences = text.replace(/```(?:json)?\s*/gi, '').replace(/```/g, '').trim();
  addCandidate(noFences);

  for (const src of [text, noFences]) {
    const objectStart = src.indexOf('{');
    const objectEnd = src.lastIndexOf('}');
    if (objectStart !== -1 && objectEnd > objectStart) {
      addCandidate(src.slice(objectStart, objectEnd + 1));
    }

    const arrayStart = src.indexOf('[');
    const arrayEnd = src.lastIndexOf(']');
    if (arrayStart !== -1 && arrayEnd > arrayStart) {
      addCandidate(src.slice(arrayStart, arrayEnd + 1));
    }

    addCandidate(extractBalancedJsonSlice(src));
  }

  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch (_) {}
  }

  throw new Error('Response was not valid JSON');
};

const firstNonEmptyString = (...vals) => {
  for (const v of vals) if (typeof v === 'string' && v.trim()) return v.trim();
  return '';
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value.map(i => typeof i === 'string' ? i.trim() : i).filter(i => String(i).trim());
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const normalizeAllahNamesPayload = (response) => {
  const raw = response?.allahNames || response?.allah_names || response?.namesOfAllah || {};
  const namesInEnglish = normalizeToArray(raw?.namesInEnglish || raw?.names_in_english || raw?.english);
  const namesInArabic  = normalizeToArray(raw?.namesInArabic  || raw?.names_in_arabic  || raw?.arabic);
  const whatItTells    = normalizeToArray(raw?.whatItTells    || raw?.what_it_tells    || raw?.explanations);
  const rowItems = Array.isArray(raw?.rows) ? raw.rows : Array.isArray(raw) ? raw : [];
  if (rowItems.length > 0) {
    return {
      namesInEnglish: rowItems.map(r => r?.namesInEnglish || r?.english || '').filter(Boolean),
      namesInArabic:  rowItems.map(r => r?.namesInArabic  || r?.arabic  || '').filter(Boolean),
      whatItTells:    rowItems.map(r => r?.whatItTells     || r?.description || '').filter(Boolean),
    };
  }
  return { namesInEnglish, namesInArabic, whatItTells };
};

// ─────────────────────────────────────────────────────────────────────────────
// createSlidesFromResponse — routes by dimension name, handles both models
// ─────────────────────────────────────────────────────────────────────────────
const createSlidesFromResponse = (response, dimensionName) => {
  const slides = [];
  const sanitize = (key) => key.replace(/[^a-zA-Z0-9_]/g, '_');

  const addSlide = (contentKey, data) => {
    const content = response[contentKey];
    const hasContent = content && (
      Array.isArray(content) ? content.length > 0
        : typeof content === 'object' ? Object.keys(content).length > 0
        : (typeof content === 'string' && content.trim())
    );
    if (hasContent) {
      const subIdx = slides.filter(s => s.dimension === dimensionName).length;
      slides.push({ id: `${dimensionName}-${sanitize(contentKey)}-${subIdx}`, dimension: dimensionName, contentKey, subtopicIndex: subIdx, ...data });
    }
  };

  // ── Shared: Objectives + Activities ───────────────────────────────────────
  if (dimensionName === 'Objectives') {
    if (response.learningObjectives) {
      slides.push({ id: `${dimensionName}-learningObjectives-0`, dimension: dimensionName, contentKey: 'learningObjectives', learningObjectives: response.learningObjectives, subtopicIndex: 0 });
    }
    return slides;
  }

  if (dimensionName === 'Activities') {
    const acts = response.activities || [];
    if (acts.length > 0) {
      slides.push({
        id: `${dimensionName}-main-0`, dimension: 'Activities', contentKey: 'main',
        activities: acts.map(a => ({ title: a.title, dimension: a.dimension, stage: a.stage, objective: a.objective, materials: a.materials || [], instructions: a.instructions || [] })),
        subtopicIndex: 0
      });
    }
    return slides;
  }

  // ── AMFI × 5DT dimensions ─────────────────────────────────────────────────
  if (dimensionName === 'Explore') {
    addSlide('content',         { content: response.content });
    addSlide('explanation',     { explanation: response.explanation });
    addSlide('observations',    { observations: response.observations });
    addSlide('fascinatingFacts',{ fascinatingFacts: response.fascinatingFacts });
    return slides;
  }

  if (dimensionName === 'Compare') {
    addSlide('analogy',     { analogy: response.analogy });
    addSlide('content',     { content: response.content });
    addSlide('explanation', { explanation: response.explanation });
    addSlide('comparison',  { comparison: response.comparison });
    return slides;
  }

  if (dimensionName === 'Question') {
    ['negation_of_chance', 'negation_of_material_causes', 'negation_of_nature', 'conclusion'].forEach((key, i) => {
      const id = `${dimensionName}-${sanitize(key)}-${i}`;
      if (key === 'conclusion' && response.conclusion?.trim()) {
        slides.push({ id, dimension: dimensionName, subtopicIndex: i, contentKey: key, conclusion: response.conclusion });
      } else if (response.questions?.[key]?.length > 0) {
        slides.push({ id, dimension: dimensionName, subtopicIndex: i, contentKey: key, questions: response.questions[key] });
      }
    });
    return slides;
  }

  if (dimensionName === 'Connect') {
    const c = response.connections || {};
    const interconnectedness = firstNonEmptyString(c.interconnectedness, c.interconnected, response.interconnectedness);
    const completeDependency  = firstNonEmptyString(c.completeDependency, c.complete_dependency, response.completeDependency);
    const interdependency     = firstNonEmptyString(c.interdependency, c.interdependence, response.interdependency);
    const names = normalizeAllahNamesPayload(response);
    let idx = 0;
    if (interconnectedness) { slides.push({ id: `${dimensionName}-interconnectedness-${idx}`, dimension: 'Connect', subtopicIndex: idx, contentKey: 'interconnectedness', interconnectedness }); idx++; }
    if (interdependency)    { slides.push({ id: `${dimensionName}-interdependency-${idx}`,    dimension: 'Connect', subtopicIndex: idx, contentKey: 'interdependency',    interdependency, completeDependency: interdependency }); idx++; }
    else if (completeDependency) { slides.push({ id: `${dimensionName}-completeDependency-${idx}`, dimension: 'Connect', subtopicIndex: idx, contentKey: 'completeDependency', completeDependency, interdependency: completeDependency }); idx++; }
    if (names.namesInEnglish.length > 0 || names.namesInArabic.length > 0 || names.whatItTells.length > 0) {
      slides.push({ id: `${dimensionName}-allahNames-${idx}`, dimension: 'Connect', subtopicIndex: idx, contentKey: 'allahNames', allahNames: names }); idx++;
    }
    return slides;
  }

  if (dimensionName === 'Appreciate') {
    addSlide('whatIfs', { whatIfs: response.whatIfs });
    if (response.zikrFikrShukr && (response.zikrFikrShukr.zikr?.length || response.zikrFikrShukr.fikr?.length)) {
      slides.push({ id: `${dimensionName}-zikrFikrShukr-${slides.length}`, dimension: 'Appreciate', contentKey: 'zikrFikrShukr', zikrFikrShukr: response.zikrFikrShukr, subtopicIndex: slides.length });
    }
    addSlide('characterLessons',  { characterLessons: response.characterLessons });
    addSlide('connectWithQuran',  { connectWithQuran: response.connectWithQuran });
    addSlide('connectWithHadith', { connectWithHadith: response.connectWithHadith });
    addSlide('dua', { dua: response.dua });
    return slides;
  }

  // ── Pure AMFI dimensions ───────────────────────────────────────────────────
  if (dimensionName === 'Tafakkur') {
    addSlide('content',         { content: response.content });
    addSlide('observations',    { observations: response.observations });
    addSlide('fascinatingFacts',{ fascinatingFacts: response.fascinatingFacts });
    if (response.muslimScholar) {
      slides.push({ id: `${dimensionName}-muslimScholar-${slides.length}`, dimension: dimensionName, contentKey: 'muslimScholar', muslimScholar: response.muslimScholar, subtopicIndex: slides.length });
    }
    if (response.creationVsDesign) {
      slides.push({ id: `${dimensionName}-creationVsDesign-${slides.length}`, dimension: dimensionName, contentKey: 'creationVsDesign', creationVsDesign: response.creationVsDesign, subtopicIndex: slides.length });
    }
    return slides;
  }

  if (dimensionName === 'Tadabbur') {
    addSlide('content',            { content: response.content });
    addSlide('reflectionQuestions',{ reflectionQuestions: response.reflectionQuestions });
    addSlide('designWisdom',       { designWisdom: response.designWisdom });
    return slides;
  }

  if (dimensionName === 'Tadhakkur') {
    if (response.interconnectedness) {
      slides.push({ id: `${dimensionName}-interconnectedness-0`, dimension: dimensionName, subtopicIndex: 0, contentKey: 'interconnectedness', interconnectedness: response.interconnectedness });
    }
    if (response.continuousCreation) {
      slides.push({ id: `${dimensionName}-continuousCreation-${slides.length}`, dimension: dimensionName, subtopicIndex: slides.length, contentKey: 'continuousCreation', continuousCreation: response.continuousCreation });
    }
    const names = normalizeAllahNamesPayload(response);
    if (names.namesInEnglish.length > 0 || names.whatItTells.length > 0) {
      slides.push({ id: `${dimensionName}-allahNames-${slides.length}`, dimension: dimensionName, subtopicIndex: slides.length, contentKey: 'allahNames', allahNames: names });
    }
    if (response.revelationAnchor) {
      slides.push({ id: `${dimensionName}-revelationAnchor-${slides.length}`, dimension: dimensionName, subtopicIndex: slides.length, contentKey: 'revelationAnchor', revelationAnchor: response.revelationAnchor });
    }
    return slides;
  }

  if (dimensionName === "Iʿtibār") {
    ['negation_of_chance', 'negation_of_material_causes', 'negation_of_autonomous_processes', 'conclusion', 'historicalLesson', 'moralLesson'].forEach((key, i) => {
      const id = `${dimensionName}-${sanitize(key)}-${i}`;
      if (['conclusion', 'historicalLesson', 'moralLesson'].includes(key) && response[key]?.trim()) {
        slides.push({ id, dimension: dimensionName, subtopicIndex: i, contentKey: key, [key]: response[key] });
      } else if (response.questions?.[key]?.length > 0) {
        slides.push({ id, dimension: dimensionName, subtopicIndex: i, contentKey: key, questions: response.questions[key] });
      }
    });
    return slides;
  }

  if (dimensionName === 'Khilāfah') {
    addSlide('whatIfs', { whatIfs: response.whatIfs });
    if (response.zikrFikrShukr && (response.zikrFikrShukr.zikr?.length || response.zikrFikrShukr.fikr?.length)) {
      slides.push({ id: `${dimensionName}-zikrFikrShukr-${slides.length}`, dimension: dimensionName, contentKey: 'zikrFikrShukr', zikrFikrShukr: response.zikrFikrShukr, subtopicIndex: slides.length });
    }
    if (response.characterLessons && typeof response.characterLessons === 'object' && !Array.isArray(response.characterLessons)) {
      slides.push({ id: `${dimensionName}-characterLessons-${slides.length}`, dimension: dimensionName, contentKey: 'characterLessons', characterLessons: response.characterLessons, subtopicIndex: slides.length });
    } else {
      addSlide('characterLessons', { characterLessons: response.characterLessons });
    }
    if (response.stewardshipActions?.length) {
      slides.push({ id: `${dimensionName}-stewardshipActions-${slides.length}`, dimension: dimensionName, contentKey: 'stewardshipActions', stewardshipActions: response.stewardshipActions, subtopicIndex: slides.length });
    }
    addSlide('connectWithQuran',  { connectWithQuran: response.connectWithQuran });
    addSlide('connectWithHadith', { connectWithHadith: response.connectWithHadith });
    addSlide('dua', { dua: response.dua });
    return slides;
  }

  return slides;
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const AmfiEdu = () => {
  const [topic, setTopic]       = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [mode, setMode]         = useState('chat');

  // Framework toggle: 'integrated' = AMFI×5DT, 'pure' = Pure AMFI
  const [framework, setFramework] = useState('integrated');

  // Chat state
  const [messages, setMessages]       = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput]     = useState('');

  // Generation state
  const [slides, setSlides]                             = useState([]);
  const [isLoading, setIsLoading]                       = useState(false);
  const [isGenerating, setIsGenerating]                 = useState(false);
  const [currentlyResettingKey, setCurrentlyResettingKey] = useState(null);
  const [genHistory, setGenHistory]                     = useState([]);
  const [error, setError]                               = useState('');

  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // ── Chat send ──────────────────────────────────────────────────────────────
  const handleSendChat = async (text) => {
    const userText = (text || chatInput).trim();
    if (!userText || isLoading) return;
    setChatInput('');
    setError('');
    const userMsg = { id: Date.now().toString(), role: 'user', content: userText };
    setMessages(prev => [...prev, userMsg]);
    const newHistory = [...chatHistory, { role: 'user', content: userText }];
    setIsLoading(true);
    try {
      const res = await createClaudeResponse({ model: AMFI_MODEL, system: AMFI_CHAT_PROMPT, messages: newHistory, max_tokens: 4096 });
      const assistantText = extractClaudeResponseText(res);
      setMessages(prev => [...prev, { id: (Date.now()+1).toString(), role: 'assistant', content: assistantText }]);
      setChatHistory([...newHistory, { role: 'assistant', content: assistantText }]);
    } catch (err) {
      setError('Could not reach the learning companion. Please check your connection and try again.');
      console.error('AMFI chat error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Update slides state ────────────────────────────────────────────────────
  const updateSlidesForDimension = (response, dimensionName, dimensions) => {
    const newSlides = createSlidesFromResponse(response, dimensionName);
    const order = dimensions.map(d => d.name);
    setSlides(prev => {
      const others = prev.filter(s => s.dimension !== dimensionName);
      const result = [];
      for (const dim of order) {
        if (dim === dimensionName) result.push(...newSlides);
        else result.push(...others.filter(s => s.dimension === dim));
      }
      return result.filter((s, i, arr) => i === arr.findIndex(t => t.id === s.id));
    });
  };

  // ── Fetch one dimension ────────────────────────────────────────────────────
  const fetchDimension = async (history, dimension, systemPrompt, dimensions, customPrompt = null) => {
    const basePrompt = customPrompt
      ? `TOPIC: ${topic}\nGRADE LEVEL: ${gradeLevel || 'not specified'}\nSTAGE/DIMENSION: ${dimension.name}\n\n${customPrompt}`
      : `TOPIC: ${topic}\nGRADE LEVEL: ${gradeLevel || 'not specified'}\nSTAGE/DIMENSION: ${dimension.name}\nReturn valid JSON only. Do not include any text outside JSON.`;

    const msgs = [...history, { role: 'user', content: basePrompt }];
    // Prefill forces Claude to start its response with '{', preventing any preamble text
    const msgsWithPrefill = [...msgs, { role: 'assistant', content: '{' }];
    const res = await createClaudeResponse({ model: AMFI_MODEL, system: systemPrompt, messages: msgsWithPrefill, max_tokens: 8096 });
    const responseText = normalizePrefilledJsonText(extractClaudeResponseText(res));
    const parsed = parseJsonPayload(responseText);
    updateSlidesForDimension(parsed, dimension.name, dimensions);
    return [...msgs, { role: 'assistant', content: responseText }];
  };

  // ── Generate all dimensions ────────────────────────────────────────────────
  const handleGenerateLesson = async () => {
    if (!topic.trim()) { setError('Please enter a topic first.'); return; }
    setError('');
    setIsGenerating(true);
    setSlides([]);
    setGenHistory([]);
    setMode('slides');

    const dims   = framework === 'pure' ? AMFI_PURE_DIMENSIONS  : AMFI_5DT_DIMENSIONS;
    const prompt = framework === 'pure' ? AMFI_PURE_GEN_PROMPT  : AMFI_5DT_GEN_PROMPT;

    let history = [];
    const failedDims = [];
    try {
      for (const dim of dims) {
        try {
          history = await fetchDimension(history, dim, prompt, dims);
        } catch (dimErr) {
          console.error(`AMFI: failed to generate ${dim.name}, skipping:`, dimErr);
          failedDims.push(dim.title);
        }
      }
      setGenHistory(history);
      if (failedDims.length > 0) {
        setError(`Some sections could not be generated: ${failedDims.join(', ')}. You can reset them individually.`);
      }
    } catch (err) {
      setError('Something went wrong generating the lesson. Please try again.');
      console.error('AMFI generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Reset a single slide ───────────────────────────────────────────────────
  const handleResetSlide = async (slideId) => {
    const slide = slides.find(s => s.id === slideId);
    if (!slide || !topic.trim()) return;
    setCurrentlyResettingKey(slideId);
    setError('');

    const dims   = framework === 'pure' ? AMFI_PURE_DIMENSIONS  : AMFI_5DT_DIMENSIONS;
    const prompt = framework === 'pure' ? AMFI_PURE_GEN_PROMPT  : AMFI_5DT_GEN_PROMPT;
    const dim    = dims.find(d => d.name === slide.dimension);
    if (!dim) { setCurrentlyResettingKey(null); return; }

    try {
      const resetPrompt = `Reset and return a new different "${slide.contentKey}" section only. Return valid JSON only.`;
      const userMsg = `TOPIC: ${topic}\nGRADE LEVEL: ${gradeLevel || 'not specified'}\nSTAGE/DIMENSION: ${dim.name}\n\n${resetPrompt}`;
      const msgs = [...genHistory, { role: 'user', content: userMsg }];
      const msgsWithPrefill = [...msgs, { role: 'assistant', content: '{' }];
      const res  = await createClaudeResponse({ model: AMFI_MODEL, system: prompt, messages: msgsWithPrefill, max_tokens: 8096 });
      const text = normalizePrefilledJsonText(extractClaudeResponseText(res));
      const parsed = parseJsonPayload(text);
      const newSlides = createSlidesFromResponse(parsed, slide.dimension);
      const match = newSlides[0];
      if (match) {
        setSlides(prev => prev.map(s => s.id === slideId
          ? { ...s, ...match, id: s.id, dimension: s.dimension, contentKey: s.contentKey, subtopicIndex: s.subtopicIndex }
          : s
        ));
      }
      setGenHistory([...msgs, { role: 'assistant', content: text }]);
    } catch (err) {
      setError('Failed to reset section. Please try again.');
      console.error('AMFI reset error:', err);
    } finally {
      setCurrentlyResettingKey(null);
    }
  };

  const handleChatKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); }
  };

  // Framework label helpers
  const frameworkLabel   = framework === 'pure' ? 'Pure AMFI' : 'AMFI × 5DT';
  const frameworkSubtitle = framework === 'pure'
    ? 'Tafakkur → Tadabbur → Tadhakkur → Iʿtibār → Khilāfah'
    : 'Tafakkur × Analytical → Tadabbur × Analogical → Iʿtibār × Critical → Tadhakkur × Meditative → Khilāfah × Moral';

  return (
    <div className="amfi-app">
      {/* Header */}
      <header className="amfi-header">
        <div className="amfi-header-left">
          <span className="amfi-header-arabic">اَلْعِلْمُ نُورٌ</span>
          <span className="amfi-header-title">AMFI Learning Companion</span>
        </div>
        <div className="amfi-header-right">
          {/* Framework toggle */}
          <div className="amfi-framework-toggle" title="Switch between AMFI × 5DT (integrated) and Pure AMFI (Qur'anic chain)">
            <button
              className={`amfi-fw-btn ${framework === 'integrated' ? 'active' : ''}`}
              onClick={() => { setFramework('integrated'); setSlides([]); }}
            >
              AMFI × 5DT
            </button>
            <button
              className={`amfi-fw-btn ${framework === 'pure' ? 'active' : ''}`}
              onClick={() => { setFramework('pure'); setSlides([]); }}
            >
              Pure AMFI
            </button>
          </div>
          {/* Mode toggle */}
          <div className="amfi-mode-toggle">
            <button className={`amfi-mode-btn ${mode === 'chat' ? 'active' : ''}`} onClick={() => setMode('chat')}>
              💬 Chat
            </button>
            <button className={`amfi-mode-btn ${mode === 'slides' ? 'active' : ''}`} onClick={() => setMode('slides')}>
              📑 Slides
            </button>
          </div>
        </div>
      </header>

      {/* Active framework indicator */}
      <div className="amfi-framework-bar">
        <span className="amfi-fw-label">{frameworkLabel}</span>
        <span className="amfi-fw-subtitle">{frameworkSubtitle}</span>
      </div>

      {/* Topic inputs */}
      <div className="amfi-inputs">
        <input
          className="amfi-topic-input"
          type="text"
          value={topic}
          onChange={e => setTopic(e.target.value)}
          placeholder="Topic (e.g. the heart, photosynthesis, soil)"
          onKeyDown={e => e.key === 'Enter' && handleGenerateLesson()}
        />
        <input
          className="amfi-grade-input"
          type="text"
          value={gradeLevel}
          onChange={e => setGradeLevel(e.target.value)}
          placeholder="Grade (optional)"
          onKeyDown={e => e.key === 'Enter' && handleGenerateLesson()}
        />
        <button
          className="amfi-generate-btn"
          onClick={handleGenerateLesson}
          disabled={isGenerating || !topic.trim()}
        >
          {isGenerating ? (
            <><FaSpinner className="amfi-spinner" /> Generating…</>
          ) : (
            `⚡ Generate — ${frameworkLabel}`
          )}
        </button>
      </div>

      {error && <p className="amfi-error-bar">{error}</p>}

      {/* Main content */}
      <main className="amfi-main">
        {mode === 'chat' ? (
          <div className="amfi-chat">
            <div className="amfi-messages">
              {messages.length === 0 && (
                <div className="amfi-empty-state">
                  <p>
                    Enter a topic above and click <strong>Generate</strong> to build a full lesson plan using{' '}
                    <strong>{frameworkLabel}</strong>, or ask a question here to explore any topic through the AMFI lens.
                  </p>
                </div>
              )}
              {messages.map(msg => (
                <div key={msg.id} className={`amfi-message ${msg.role}`}>
                  <div className="amfi-message-bubble">
                    {msg.role === 'assistant'
                      ? <RenderMarkdown markdown={msg.content} />
                      : <p>{msg.content}</p>
                    }
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="amfi-message assistant">
                  <div className="amfi-message-bubble amfi-typing"><span /><span /><span /></div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
            <div className="amfi-input-bar">
              <textarea
                className="amfi-input"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={handleChatKey}
                placeholder="Ask a question about any topic…"
                rows={1}
                disabled={isLoading}
              />
              <button className="amfi-send-btn" onClick={() => handleSendChat()} disabled={isLoading || !chatInput.trim()}>→</button>
            </div>
          </div>
        ) : (
          <div className="amfi-slides-panel">
            {isGenerating && (
              <div className="amfi-generating-indicator">
                <FaSpinner className="amfi-spinner" /> Generating {frameworkLabel} lesson… {slides.length > 0 && `(${slides.length} sections ready)`}
              </div>
            )}
            {slides.length === 0 && !isGenerating ? (
              <div className="amfi-slides-empty">
                <p>No lesson generated yet. Enter a topic above and click <strong>Generate — {frameworkLabel}</strong>.</p>
              </div>
            ) : (
              <div className="amfi-slide-content">
                {slides.length > 0 && !isGenerating && (
                  <div className="amfi-export-bar">
                    <button
                      className="amfi-export-btn"
                      onClick={() => exportAmfiAsWord(slides, topic, framework)}
                      title="Download as Word document"
                    >
                      <FaFileWord style={{ marginRight: 6 }} /> Export Word
                    </button>
                    <button
                      className="amfi-export-btn amfi-export-btn--print"
                      onClick={() => exportAmfiAsPptx(slides, topic, framework)}
                      title="Download as PowerPoint"
                    >
                      <FaFilePowerpoint style={{ marginRight: 6 }} /> Export PPTX
                    </button>
                  </div>
                )}
                {slides.map((slide, i) => (
                  <AmfiSlideContent
                    key={slide.id || i}
                    slide={slide}
                    onResetDimension={handleResetSlide}
                    isResettingThisSlide={currentlyResettingKey === slide.id}
                    framework={framework}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AmfiEdu;
